import type { UpdateContactData } from '@/features/Contact/types/contact.types';
import {
    syncMyRegisterEntityDirectorProfileHistory,
    updateMyRegisterEntityDirectorOfficer,
} from '../api/myRegisterApi';
import {
    appendPassportExpiryToContactUpdate,
    appendRegisterCountryCdToContactUpdate,
    buildRegisterProfileForHistorySync,
    hasRegisterCessationDate,
    resolveRegisterGenderForContact,
} from './mapContactToRegisterCommon';
import { buildMyRegisterDirectorUpdatePayload } from './myRegisterOfficerOccupation';
import { isCorporateRegisterCategory } from '../constants/myRegisterPositionCd';
import { assertRegisterAppointmentDate } from './registerAddValidation';
import {
    runMyRegisterOfficerSaveWithProfileHistory,
    type MyRegisterOfficerSaveSnapshot,
} from './saveMyRegisterOfficerWithProfileHistory';
import { saveMyRegisterContactProfileAndAddresses } from './saveMyRegisterRoleContactAddresses';

export const MY_REGISTER_DIRECTOR_POSITION_CD = 'B30027';

export type EntityDirectorAddressSaveRefs = {
    addressData: Record<string, unknown> | null;
    contactInfoData: Record<string, unknown> | null;
    useBranchAddresses: boolean;
    getResidentialBranchListForSave: () => unknown[] | null;
    getBranchAddressListsForSave: () => {
        serviceAddressList: unknown[];
        businessAddressList: unknown[];
    } | null;
};

/** DirectorSection jsonData → Global Contact PUT payload (인적사항 + 프로필) */
export function buildContactUpdateFromDirectorJson(
    jsonData: Record<string, unknown>,
): UpdateContactData & Record<string, unknown> {
    const updateData: UpdateContactData & Record<string, unknown> = {};

    if (jsonData.ID_TYPE_CD !== undefined) {
        updateData.identificationType = String(jsonData.ID_TYPE_CD ?? '');
    }
    if (jsonData.ID_TYPE !== undefined) {
        updateData.idTypeJson = String(jsonData.ID_TYPE ?? '');
    }
    if (jsonData.CONTACT_REG_NO !== undefined) {
        updateData.identificationNo = String(jsonData.CONTACT_REG_NO ?? '');
    }
    if (jsonData.NATIONALITY_CD !== undefined) {
        updateData.nationality = String(jsonData.NATIONALITY_CD ?? '');
    }
    if (jsonData.DATE_OF_BIRTH !== undefined) {
        const dob = String(jsonData.DATE_OF_BIRTH ?? '').trim();
        updateData.dateOfBirth = dob || undefined;
    }
    if (jsonData.CONTACT_NAME !== undefined) {
        updateData.entityName = String(jsonData.CONTACT_NAME ?? '');
    }
    if (jsonData.CONTACT_EMAIL !== undefined) {
        const email = String(jsonData.CONTACT_EMAIL ?? '').trim();
        updateData.email = email;
        updateData.emails = email ? [email] : [];
        updateData.emailMainYn = email ? [true] : [];
        updateData.emailSyncYn = email ? [1] : [];
    }
    if (jsonData.MOBILE_NO !== undefined) {
        updateData.mobileNo = String(jsonData.MOBILE_NO ?? '');
    }
  if (jsonData.AREA_CODE !== undefined) {
    updateData.areaCode = String(jsonData.AREA_CODE ?? '');
  }
  appendRegisterCountryCdToContactUpdate(jsonData, updateData);

  const gender = resolveRegisterGenderForContact(jsonData);
    if (gender !== undefined) updateData.profileGender = gender;

    const race = String(jsonData.RACE ?? jsonData.RACE_TEXT ?? '').trim();
    if (jsonData.RACE !== undefined || jsonData.RACE_TEXT !== undefined) {
        updateData.profileRace = race;
    }

    if (jsonData.ISSUING_COUNTRY !== undefined) {
        updateData.profileIssuingCountry = String(jsonData.ISSUING_COUNTRY ?? '');
    }
    if (jsonData.PLACE_OF_BIRTH !== undefined) {
        updateData.profilePlaceOfBirth = String(jsonData.PLACE_OF_BIRTH ?? '');
    }
    if (jsonData.IS_MEETING !== undefined) {
        const raw = jsonData.IS_MEETING;
        if (raw === null || raw === '') {
            updateData.profileIsMeeting = null;
        } else {
            updateData.profileIsMeeting =
                raw === 1 || raw === true || raw === '1' || String(raw).toLowerCase() === 'true' ? 1 : 0;
        }
    }

    if (
        isCorporateRegisterCategory(jsonData.CATEGORY_CD)
        && jsonData.NEW_REG_NO !== undefined
    ) {
        updateData.profileNewRegNo = String(jsonData.NEW_REG_NO ?? '').trim();
    }

    // 법인 설립지/설립일 — 컨택트(L_GLOBAL_CONTACT) 단일 소스에 저장.
    //   레지스터/엔티티 멤버 상세에서 편집 시 이 값이 contactsApi.updateContact → GC.JURISDICTION / DATE_OF_INCORPORATION 로 반영된다.
    //   jsonData.JURISDICTION 은 모달에서 B15 코드로 정규화(normalizeCodeFieldsForBinding)되어 들어온다.
    if (jsonData.JURISDICTION !== undefined) {
        updateData.jurisdiction = String(jsonData.JURISDICTION ?? '').trim();
    }
    if (jsonData.DATE_OF_INCORPORATION !== undefined) {
        updateData.incorporationDate = String(jsonData.DATE_OF_INCORPORATION ?? '').trim() || undefined;
    }

    appendPassportExpiryToContactUpdate(jsonData, updateData);

    return updateData;
}

function strField(v: unknown): string {
    return String(v ?? '').trim();
}

/** Global Contact 주소 블록 없이 레지스터 jsonData 주소 필드만 있을 때 (퇴임 history UI 등) */
function buildLocalAddressFromRegisterJson(
    jsonData: Record<string, unknown>,
    prefix: string,
): Record<string, string> | undefined {
    const p = prefix ? `${prefix}_` : '';
    const line1 = strField(jsonData[`${p}STREET01`] ?? jsonData[`${p}ADDRESS_LINE_1`]);
    const postCode = strField(jsonData[`${p}POST_CODE`] ?? jsonData[`${p}POSTCODE`]);
    if (!line1 && !postCode) return undefined;
    return {
        addressLine1: line1,
        addressLine2: strField(jsonData[`${p}STREET02`] ?? jsonData[`${p}ADDRESS_LINE_2`]),
        addressLine3: strField(jsonData[`${p}STREET03`] ?? jsonData[`${p}ADDRESS_LINE_3`]),
        postcode: postCode,
        post_code: postCode,
        city: strField(jsonData[`${p}CITY`]),
        state: strField(jsonData[`${p}STATE`]),
        country: strField(jsonData[`${p}COUNTRY`]),
    };
}

function mergeRegisterJsonAddressFallback(
    updateData: UpdateContactData & Record<string, unknown>,
    entityCd: string,
    jsonData: Record<string, unknown>,
    positionCd: string,
    positionName: string,
): UpdateContactData & Record<string, unknown> {
    const localAddress = buildLocalAddressFromRegisterJson(jsonData, '');
    const serviceAddress = buildLocalAddressFromRegisterJson(jsonData, 'SERVICE');
    const businessAddress = buildLocalAddressFromRegisterJson(jsonData, 'BUS');
    if (!localAddress && !serviceAddress && !businessAddress) return updateData;

    const merged = { ...updateData };
    merged.entityCd = entityCd;
    merged.branchEntityCd = entityCd;
    merged.positionCd = positionCd;
    merged.positionName = positionName;
    merged.addressTypeCd = strField(jsonData.ADDRESS_TYPE_CD) || 'B07001';

    if (localAddress) {
        merged.localAddress = localAddress;
        merged.skipResidentialJsonDataWrite = false;
    }
    if (serviceAddress) {
        merged.serviceAddressList = [{
            ...serviceAddress,
            street01: serviceAddress.addressLine1,
            street02: serviceAddress.addressLine2,
            street03: serviceAddress.addressLine3,
            entity_cd: entityCd,
            position: positionName,
        }];
    }
    if (businessAddress) {
        merged.businessAddressList = [{
            ...businessAddress,
            street01: businessAddress.addressLine1,
            street02: businessAddress.addressLine2,
            street03: businessAddress.addressLine3,
            entity_cd: entityCd,
            position: positionName,
        }];
    }
    return merged;
}

/** @deprecated Register 저장은 saveMyRegisterRoleContactAddresses 사용. json fallback 전용 */
export function mergeAddressIntoContactUpdate(
    base: UpdateContactData & Record<string, unknown>,
    entityCd: string,
    refs: EntityDirectorAddressSaveRefs | null,
    jsonData?: Record<string, unknown>,
    positionCtx?: { positionCd?: string; positionName?: string },
): UpdateContactData & Record<string, unknown> {
    const positionCd = positionCtx?.positionCd ?? MY_REGISTER_DIRECTOR_POSITION_CD;
    const positionName = positionCtx?.positionName ?? 'Director';

    if (!refs) {
        if (jsonData) {
            return mergeRegisterJsonAddressFallback(
                base,
                entityCd,
                jsonData,
                positionCd,
                positionName,
            );
        }
        return base;
    }

    const updateData = { ...base };
    updateData.entityCd = entityCd;
    updateData.branchEntityCd = entityCd;
    updateData.positionCd = positionCd;
    updateData.positionName = positionName;

    const addressData = refs.addressData;
    const contactInfo = refs.contactInfoData;
    const addressType = String(
        (addressData as Record<string, unknown> | null)?.addressType
            ?? contactInfo?.ADDRESS_TYPE_CD
            ?? 'B07001',
    );

    updateData.addressTypeCd = addressType;

    const localAddress = (addressData as Record<string, unknown> | null)?.localAddress as Record<string, unknown> | undefined;
    const foreignAddress = (addressData as Record<string, unknown> | null)?.foreignAddress as Record<string, unknown> | undefined;

    if (localAddress) {
        updateData.localAddress = { ...localAddress };
    }
    if (foreignAddress) {
        updateData.foreignAddress = { ...foreignAddress };
    }

    const altLocal = contactInfo?.altLocalAddress as Record<string, unknown> | undefined;
    if (altLocal) {
        updateData.altLocalAddress = { ...altLocal };
    }

    if (Array.isArray(contactInfo?.serviceAddressList)) {
        updateData.serviceAddressList = (contactInfo.serviceAddressList as unknown[]).map((row) => ({ ...(row as object) }));
    }
    if (Array.isArray(contactInfo?.businessAddressList)) {
        updateData.businessAddressList = (contactInfo.businessAddressList as unknown[]).map((row) => ({ ...(row as object) }));
    }

    if (refs.useBranchAddresses) {
        const resDraft = refs.getResidentialBranchListForSave() ?? [];
        updateData.useBranchAddresses = true;
        updateData.skipResidentialJsonDataWrite = true;
        updateData.residentialAddressList = Array.isArray(resDraft)
            ? resDraft.map((row) => ({ ...(row as object) }))
            : [];

        const branchLists = refs.getBranchAddressListsForSave();
        if (branchLists) {
            updateData.serviceAddressList = branchLists.serviceAddressList.map((row) => ({ ...(row as object) }));
            updateData.businessAddressList = branchLists.businessAddressList.map((row) => ({ ...(row as object) }));
        }

        const primary = (updateData.residentialAddressList as Record<string, unknown>[])?.[0];
        if (primary) {
            const str = (v: unknown) => String(v ?? '');
            updateData.localAddress = {
                addressLine1: str(primary.street01 || primary.addressLine1),
                addressLine2: str(primary.street02 || primary.addressLine2),
                addressLine3: str(primary.street03 || primary.addressLine3),
                country: str(primary.country || primary.countryCode),
                state: str(primary.state || primary.stateCode),
                city: str(primary.city || primary.cityCode),
                postcode: str(primary.post_code || primary.postcode || primary.postalCode),
                post_code: str(primary.post_code || primary.postcode || primary.postalCode),
                countryCode: str(primary.countryCode || primary.country),
                stateCode: str(primary.stateCode || primary.state),
                cityCode: str(primary.cityCode || primary.city),
                postcodeCode: str(primary.postcodeCode || primary.post_code || primary.postcode),
            };
        }
    } else if (localAddress) {
        updateData.skipResidentialJsonDataWrite = false;
    }

    return updateData;
}

export type SaveMyRegisterEntityDirectorParams = {
    entityCd: string;
    officerIdx: number;
    contactCd: string;
    jsonData: Record<string, unknown>;
    addressRefs?: EntityDirectorAddressSaveRefs | null;
    officerDatesSnapshot?: MyRegisterOfficerSaveSnapshot | null;
};

/**
 * MY Register Entity Director 저장
 * 1. L_OFFICER 임명일/퇴임일 선저장
 * 2. 재직 중: Global Contact (+ Profile + 주소)
 * 3. 퇴임: 역할 메타만 저장, live Contact 미갱신 + profile history 스냅샷
 */
export async function saveMyRegisterEntityDirector(params: SaveMyRegisterEntityDirectorParams): Promise<void> {
    const entityCd = String(params.entityCd || '').trim();
    const contactCd = String(params.contactCd || '').trim();
    const officerIdx = Number(params.officerIdx);
    const jsonData = params.jsonData || {};

    if (!entityCd || !contactCd || !officerIdx) {
        throw new Error('entityCd, contactCd, and officerIdx are required');
    }

    assertRegisterAppointmentDate(jsonData);

    const appointmentDate = String(jsonData.APPOINTMENT_DATE ?? '').trim() || null;
    const cessationDate = String(
        jsonData.CESSATION_DATE ?? jsonData.cessationDate ?? '',
    ).trim() || null;
    const officerUpdate = buildMyRegisterDirectorUpdatePayload(jsonData);

    const contactUpdate = buildContactUpdateFromDirectorJson(jsonData);

    await runMyRegisterOfficerSaveWithProfileHistory({
        entityCd,
        officerIdx,
        appointmentDate,
        cessationDate,
        officerUpdatePayload: officerUpdate,
        officerDatesSnapshot: params.officerDatesSnapshot,
        registerProfile: hasRegisterCessationDate(cessationDate)
            ? buildRegisterProfileForHistorySync(jsonData)
            : null,
        updateOfficer: (ent, idx, payload) =>
            updateMyRegisterEntityDirectorOfficer(ent, idx, payload),
        updateContact: () => saveMyRegisterContactProfileAndAddresses({
            entityCd,
            contactCd,
            positionCd: MY_REGISTER_DIRECTOR_POSITION_CD,
            positionName: 'Director',
            contactUpdate,
            refs: params.addressRefs ?? null,
            jsonData,
        }),
        syncProfileHistory: syncMyRegisterEntityDirectorProfileHistory,
    });
}
